import { ICreative } from '@/shared/api'
import { cn } from '@/true-fsd/shared/utils/css'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    Toast,
} from '@/shared/ui'

import {
    useState,
    forwardRef,
    ButtonHTMLAttributes,
    useCallback,
    CSSProperties,
} from 'react'

import { CreativeBadge } from './CreativeBadge'
import { CreativesList } from './CreativesList'
import { useOptimisticValue } from '@/shared/hooks'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'

export type CreativeSelectTriggerProps = Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'className'
>
export const CreativeSelectTrigger = forwardRef<
    HTMLButtonElement,
    CreativeSelectTriggerProps
>(function CreativeSelectTrigger({ disabled, ...props }, ref) {
    return (
        <button
            {...props}
            disabled={disabled}
            ref={ref}
            type="button"
            className={cn(
                'flex-grow',
                'flex items-center justify-between rounded-md border border-[#CCCCCC] px-3 py-2 text-[12px]',
                disabled ? 'opacity-50' : 'hover:bg-gray-100',
                'h-[30px]'
            )}
        >
            Выбрать креатив <span className="text-lg">+</span>
        </button>
    )
})

const useCreativeSelect = ({
    activeCreativeActual,
    onTouched,
}: {
    activeCreativeActual: ICreative | null
    onTouched?: () => void
}) => {
    const [toastMessage, setToastMessage] = useState('')
    const [open, setOpen] = useState(false)

    const onClose = () => {
        setOpen(false)
        if (onTouched) onTouched()
    }

    const onOpenChange = (v: boolean) => {
        setOpen(v)
        if (onTouched) onTouched()
    }

    const {
        value: activeCreative,
        setOptimisticValue: setOptimisticActiveCreative,
    } = useOptimisticValue(
        activeCreativeActual,
        useCallback(
            (a: ICreative | null, b: ICreative | null) => a?.id === b?.id,
            []
        )
    )

    const onSelectActiveCreative = (newActive: ICreative) => {
        setOptimisticActiveCreative(newActive)
        onClose()
        if (onTouched) onTouched()
    }

    return {
        toastMessage,
        setToastMessage,

        open,
        onOpenChange,
        onClose,

        activeCreative,
        onSelectActiveCreative,
    }
}

export type CreativeSelectProps = {
    onTouched?: () => void
    activeCreative: ICreative | null
    groupId: number
    triggerStyle?: CSSProperties
} & WorkspaceWithLinkDescriptor
export const CreativeSelect = ({
    onTouched,
    activeCreative: activeCreativeActual,
    groupId,
    triggerStyle,
    ...props
}: CreativeSelectProps) => {
    const {
        toastMessage,
        setToastMessage,

        open,
        onOpenChange,

        activeCreative,
        onSelectActiveCreative,
    } = useCreativeSelect({ activeCreativeActual, onTouched })

    const trigger = activeCreative ? (
        <button className={'min-w-0 flex-grow'}>
            <CreativeBadge data={activeCreative} withHover />
        </button>
    ) : (
        <CreativeSelectTrigger style={triggerStyle} />
    )

    return (
        <>
            <DropdownMenu {...{ open, onOpenChange }}>
                <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>

                <DropdownMenuContent
                    side="right"
                    align="start"
                    sideOffset={4}
                    alignOffset={-14}
                    className="z-50 max-h-[400px] w-full max-w-[320px] overflow-y-auto bg-white p-1"
                >
                    {open && (
                        <CreativesList
                            {...{
                                ...props,
                                groupId,
                                activeCreative,
                                onSelectActiveCreative,
                            }}
                            onToastMessage={setToastMessage}
                        />
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            <Toast
                message={toastMessage}
                visible={!!toastMessage}
                onClose={() => setToastMessage('')}
                greenSwooshIcon={true}
            />
        </>
    )
}
